import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * 前端用户类型定义
 * 
 * 注意：与后端 UserInfo 类型保持一致
 */
export interface User {
  id: string;
  username: string | null;
  nickname?: string;
  realname?: string;
  email?: string;
  mobile?: string | null;
  avatarUrl?: string;
  status?: string;
  type?: string;
  tenantId?: string;
  roles?: string[];
  apps?: {
    groups: Array<{
      type: string;
      label: string;
      apps: Array<{
        id: string;
        code: string;
        name: string;
        description?: string | null;
        icon?: string | null;
        routePath?: string | null;
        componentPath?: string | null;
        type: string;
        status: string;
        isVisible?: boolean | null;
        order: number;
      }>;
    }>;
  };
  spaces?: Array<{
    id: string;
    name: string;
    code: string | null;
    type: string;
    /** 空间类型（PERSONAL / TEAM / ENTERPRISE） */
    isPlatform: boolean;
    status: string | null;
    isVerified: boolean;
    logoUrl: string | null;
    ownerId: string;
    memberLimit?: number;
    createdAt: string;
    updatedAt: string;
    subscription?: {
      id: string;
      planCode?: string;
      planName?: string;
      startTime: string;
      endTime: string | null;
      status: string;
      autoRenew: boolean;
      renewNoticeSent: boolean;
      suspendReason: string | null;
      suspendTime: string | null;
      cancelReason: string | null;
      cancelTime: string | null;
      createdAt: string;
      updatedAt: string;
    } | null;
  }>;
}

interface AuthState {
  user: User | null;
  token: string | null; // Access Token（仅移动端使用）
  refreshToken: string | null; // Refresh Token（仅移动端使用）
  isAuthenticated: boolean;
  isHydrated: boolean; // 标记是否已完成客户端初始化
}

// 服务端和客户端使用相同的初始状态，避免 hydration mismatch
const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isHydrated: false,
};

/**
 * 判断是否为 Web 端（Cookie 认证）
 * 
 * Web 端：home、admin（使用 Cookie 认证，SSO 支持）
 * 移动端：mobile（使用 localStorage + Header 认证）
 */
function isWebPlatform(): boolean {
  if (typeof window === 'undefined') return false;
  
  // 判断是否为 Web 端（通过 User-Agent 或环境变量）
  // 移动端会有特定的标识（如 React Native）
  const userAgent = window.navigator.userAgent;
  const isReactNative = /ReactNative/i.test(userAgent);
  
  // 如果是 React Native，则为移动端
  if (isReactNative) return false;
  
  // 否则为 Web 端
  return true;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 客户端初始化：从 localStorage 或 Cookie 恢复认证状态
    hydrate: (state) => {
      if (typeof window !== 'undefined') {
        const isWeb = isWebPlatform();
        
        if (isWeb) {
          // ✅ Web 端（Cookie 认证）
          // Cookie 由浏览器自动管理，无需手动读取
          // 只需要检查是否有认证状态（通过尝试获取用户信息）
          // 这里先标记为未认证，由 AuthProvider 检查 Cookie 并获取用户信息
          state.isAuthenticated = false;
          state.token = null;
          state.refreshToken = null;
        } else {
          // ✅ 移动端（localStorage + Header 认证）
          const token = localStorage.getItem('token');
          const refreshToken = localStorage.getItem('refreshToken');
          if (token) {
            state.token = token;
            state.isAuthenticated = true;
          } else {
            state.token = null;
            state.isAuthenticated = false;
            state.user = null;
          }
          if (refreshToken) {
            state.refreshToken = refreshToken;
          } else {
            state.refreshToken = null;
          }
        }
      }
      // 标记客户端初始化完成（只在第一次调用时设置）
      if (!state.isHydrated) {
        state.isHydrated = true;
      }
    },
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; token?: string; refreshToken?: string }>
    ) => {
      state.user = action.payload.user;
      state.isAuthenticated = true;
      
      if (typeof window !== 'undefined') {
        const isWeb = isWebPlatform();
        
        if (isWeb) {
          // ✅ Web 端（Cookie 认证）
          // Token 已存储在 Cookie 中，无需手动存储
          // 只保存用户信息到 state
          state.token = null;
          state.refreshToken = null;
        } else {
          // ✅ 移动端（localStorage + Header 认证）
          if (action.payload.token) {
            state.token = action.payload.token;
            localStorage.setItem('token', action.payload.token);
          }
          if (action.payload.refreshToken) {
            state.refreshToken = action.payload.refreshToken;
            localStorage.setItem('refreshToken', action.payload.refreshToken);
          }
        }
      }
    },
    // 更新用户信息（用于刷新权限、角色、模块）
    updateUserInfo: (state, action: PayloadAction<{ user: User }>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload.user };
      }
    },
    // 刷新 Access Token
    refreshAccessToken: (
      state,
      action: PayloadAction<{ token?: string; refreshToken?: string }>
    ) => {
      if (typeof window !== 'undefined') {
        const isWeb = isWebPlatform();
        
        if (isWeb) {
          // ✅ Web 端（Cookie 认证）
          // Token 已在 Cookie 中更新，无需手动处理
          state.token = null;
          state.refreshToken = null;
        } else {
          // ✅ 移动端（localStorage + Header 认证）
          if (action.payload.token) {
            state.token = action.payload.token;
            localStorage.setItem('token', action.payload.token);
          }
          if (action.payload.refreshToken) {
            state.refreshToken = action.payload.refreshToken;
            localStorage.setItem('refreshToken', action.payload.refreshToken);
          }
        }
      }
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      
      if (typeof window !== 'undefined') {
        const isWeb = isWebPlatform();
        
        if (isWeb) {
          // ✅ Web 端（Cookie 认证）
          // Cookie 由后端登出接口清除，前端无需操作
          // 但为了安全，也清除 localStorage（如果有的话）
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        } else {
          // ✅ 移动端（localStorage + Header 认证）
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
    },
  },
});

export const { hydrate, setCredentials, refreshAccessToken, logout, updateUserInfo } = authSlice.actions;
export default authSlice.reducer;

